/**
 * Mutation registry — single source of truth for what query keys each
 * entity-action invalidates. Replaces the action→prefix lookup that lived
 * in the standalone invalidationMap.ts.
 *
 * Entity names are type-safe; action names are constrained per entity.
 * Roots are imported from the entity files that declare them — never
 * re-typed as literals here (ADR 0010).
 */

import { ACTORS_ROOT as ACTORS } from '@/data/entities/actors';
import { FOUNTAIN_ACTS_ROOT as FOUNTAIN_ACTS } from '@/data/entities/acts';
import { BITS_ROOT as BITS } from '@/data/entities/bits';
import { CHARACTERS_ROOT as CHARACTERS } from '@/data/entities/characters';
import { COMMENTS_ROOT as COMMENTS } from '@/data/entities/comments';
import {
    EPISODE_PRESENTATIONS_ROOT as EPISODE_PRESENTATIONS,
    EPISODE_PRESENTATIONS_TITLE_ROOT as EPISODE_PRESENTATIONS_TITLE,
    EPISODES_ROOT as EPISODES,
    EPISODES_SEARCH_ROOT as EPISODES_SEARCH,
} from '@/data/entities/episodes';
import { FEATURES_ROOT as FEATURES } from '@/data/entities/features';
import { FILTERS_ROOT as FILTERS } from '@/data/entities/filters';
import { FOUNTAIN_SCENES_ROOT as FOUNTAIN_SCENES } from '@/data/entities/fountainScenes';
import { FOUNTAIN_FRAGMENTS_ROOT as FOUNTAIN_FRAGMENTS } from '@/data/entities/fragments';
import { LINE_PRESENTATIONS_ROOT as LINE_PRESENTATIONS, LINES_ROOT as LINES } from '@/data/entities/lines';
import { NOTIFICATIONS_ROOT as NOTIFICATIONS } from '@/data/entities/notifications';
import { PROJECTS_ROOT as PROJECTS } from '@/data/entities/projects';
import { RELATIONSHIPS_ROOT as RELATIONSHIPS } from '@/data/entities/relationships';
import { RELATION_TYPES_ROOT as RELATION_TYPES } from '@/data/entities/relationTypes';
import { SCENE_NOTES_ROOT as SCENE_NOTES } from '@/data/entities/sceneNotes';
import { SCENES_ROOT as SCENES } from '@/data/entities/scenes';
import { GLOBAL_SEARCH_ROOT as GLOBAL_SEARCH } from '@/data/entities/search';
import { SNAPSHOTS_ROOT as SNAPSHOTS } from '@/data/entities/snapshots';
import { SPELLING_WORDS_ROOT as SPELLING_WORDS } from '@/data/entities/spellingWords';
import { TAGS_ROOT as TAGS } from '@/data/entities/tags';
import { USER_TUTORIAL_ROOT as USER_TUTORIAL } from '@/data/entities/userTutorial';

type Prefix = readonly unknown[];
type PrefixList = readonly Prefix[];

// --- Roots are imported from the entity files that own them (ADR 0010).
// The one literal below covers a noun that has no entity yet (nothing keys
// queries under it today); it becomes an entity-owned root when an assets
// read exists. Do not add new literals here — declare the root on the entity. ---
const ASSETS = ['assets'] as const;

export const ENTITY_NAMES = [
    'scene',
    'act',
    'fragment',
    'character',
    'relationship',
    'relationType',
    'episode',
    'feature',
    'tag',
    'bit',
    'textAtom',
    'sceneNote',
    'actNote',
    'fragmentNote',
    'project',
    'comment',
    'notification',
    'snapshot',
    'characterLine',
    'asset',
    'actor',
    'line',
    'spellingWord',
    'userTutorial',
] as const;

export type EntityName = (typeof ENTITY_NAMES)[number];

/**
 * Per-entity action → invalidation prefixes.
 * Each entry mirrors a row in the (deprecated) invalidationMap.ts.
 */
export interface EntityActions {
    scene:
        | 'create'
        | 'update'
        | 'updateMeta'
        | 'updateTags'
        | 'move'
        | 'setDraft'
        | 'delete'
        | 'bulkMove'
        | 'updateLogline'
        | 'updateSynopsis'
        | 'setTextRowDraft'
        | 'addTextRow'
        | 'removeTextRow'
        | 'saveContent';
    act: 'create' | 'update' | 'delete' | 'updateLogline' | 'updateSynopsis' | 'setDraft' | 'addTextRow' | 'removeTextRow';
    fragment: 'create' | 'update' | 'delete' | 'updateLogline' | 'updateSynopsis' | 'setDraft' | 'addTextRow' | 'removeTextRow';
    character: 'create' | 'update' | 'delete';
    relationship: 'create' | 'update' | 'delete';
    relationType: 'create' | 'delete';
    episode: 'create' | 'update' | 'delete';
    feature: 'create' | 'update' | 'delete';
    tag: 'create' | 'update' | 'updateColor' | 'delete';
    bit: 'create' | 'update' | 'delete';
    textAtom: 'create' | 'update' | 'delete';
    sceneNote: 'create' | 'updateBody' | 'delete';
    actNote: 'create' | 'updateBody' | 'delete';
    fragmentNote: 'create' | 'updateBody' | 'delete';
    project: 'create' | 'update' | 'delete' | 'archive' | 'restore';
    comment: 'create' | 'update' | 'delete' | 'createChild';
    notification: 'update' | 'delete';
    snapshot: 'create';
    characterLine: 'create' | 'update' | 'delete';
    asset: 'create' | 'update' | 'delete';
    actor: 'create' | 'update' | 'delete' | 'attach' | 'detach';
    line: 'create' | 'update' | 'delete';
    spellingWord: 'create';
    userTutorial: 'update';
}

type RegistryShape = {
    [E in EntityName]: { [A in EntityActions[E]]: PrefixList };
};

export const registry: RegistryShape = {
    scene: {
        create: [SCENES, EPISODES, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, EPISODE_PRESENTATIONS, EPISODES_SEARCH, LINE_PRESENTATIONS],
        update: [SCENES, FOUNTAIN_SCENES],
        updateMeta: [SCENES, FOUNTAIN_SCENES, EPISODE_PRESENTATIONS, EPISODES_SEARCH],
        // Tag attach/detach on a scene. Row pins the union that fired before
        // ADR 0013 (updateMeta row + sceneUpdateInvalidate shotgun + inline
        // TAGS/LINE_PRESENTATIONS); narrow deliberately, not silently.
        // CHARACTERS: attaching/detaching a character tag changes each
        // character's scenes_count/relations (which gate full-delete) — mirrors
        // the entityTag.attach cross-cutting row (#1815).
        updateTags: [
            SCENES,
            FOUNTAIN_SCENES,
            EPISODE_PRESENTATIONS,
            EPISODES_SEARCH,
            EPISODES,
            LINE_PRESENTATIONS,
            FILTERS,
            TAGS,
            CHARACTERS,
        ],
        // Single-scene act/position move (drag reorder). Same pinned-union
        // rationale as updateTags, minus TAGS (a move never changes tags).
        move: [SCENES, FOUNTAIN_SCENES, EPISODE_PRESENTATIONS, EPISODES_SEARCH, EPISODES, LINE_PRESENTATIONS, FILTERS],
        // «Черновик» on a whole scene (#1840-06). Purpose-named rather than
        // folded into updateMeta because its fan-out is a different fact: the
        // flag RENUMBERS the episode, so every read that denormalises a scene
        // `code` is stale. That is the same set `move` refreshes (a reorder
        // renumbers too, since #1840-05) — EPISODES because `IEpisode` embeds
        // its scenes — plus GLOBAL_SEARCH, whose cached result set prints
        // those codes as its location line. FILTERS and TAGS stay out: a draft
        // flag changes neither the facet lists nor any chip.
        setDraft: [SCENES, FOUNTAIN_SCENES, EPISODE_PRESENTATIONS, EPISODES_SEARCH, EPISODES, LINE_PRESENTATIONS, GLOBAL_SEARCH],
        delete: [SCENES, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS],
        // Bulk moves land scenes on other lines/episodes, so line presentations
        // refresh too (was compensated inline by every SceneBulkMove host).
        bulkMove: [SCENES, FOUNTAIN_SCENES, EPISODE_PRESENTATIONS, EPISODES_SEARCH, LINE_PRESENTATIONS],
        updateLogline: [SCENES, FOUNTAIN_SCENES],
        updateSynopsis: [SCENES, FOUNTAIN_SCENES],
        // «В черновик» on one of a scene's logline / plot ROWS, and a second
        // row added or an extra deleted (#1841 item 12).
        //
        // Three rows rather than a share of `setDraft` above: that one is the
        // WHOLE scene's flag, which renumbers the episode and so refreshes
        // seven reads. Which of a scene's loglines is active changes no code
        // and no position — it changes the logline, exactly as writing it does,
        // so the fan-out is `updateLogline`'s: the plan's read and the editor's.
        setTextRowDraft: [SCENES, FOUNTAIN_SCENES],
        addTextRow: [SCENES, FOUNTAIN_SCENES],
        removeTextRow: [SCENES, FOUNTAIN_SCENES],
        // The editor's own save of a scene's Fountain text (body + logline +
        // plot, `useUpdateScene`). `textAtom.update`'s fan-out minus the two
        // reads the editor sits on: FOUNTAIN_SCENES is written directly by the
        // save, and a refetch of it asked mid-save answers with the text from
        // before the save and lands after it (#1841, 22.09 item 4);
        // FOUNTAIN_FRAGMENTS holds the episode rows, which a scene's text does
        // not touch, and refetching it clobbers an episode logline being saved.
        saveContent: [SCENES, COMMENTS, EPISODES],
    },
    act: {
        create: [FOUNTAIN_ACTS, PROJECTS],
        update: [FOUNTAIN_ACTS],
        delete: [FOUNTAIN_ACTS, FOUNTAIN_FRAGMENTS, FOUNTAIN_SCENES, PROJECTS],
        updateLogline: [FOUNTAIN_ACTS],
        updateSynopsis: [FOUNTAIN_ACTS],
        // The «в черновик» flag of an act's logline / plot ROW (#1840-15).
        //
        // Its own row rather than `textAtom.update`, for the reason `sceneNote`
        // has its own family: the flag rides a text atom, but its fan-out is
        // somewhere else entirely. The act read is the only one that goes stale
        // — the flag changes no code, no position and nothing any other read
        // denormalises — while `textAtom.update` would refetch every scene in
        // the project (SCENES, FOUNTAIN_SCENES, COMMENTS, EPISODES) and re-sync
        // the document mid-edit on a keystroke.
        setDraft: [FOUNTAIN_ACTS],
        // A second logline / plot added to the act, and an extra one deleted
        // (#1841 item 11). Same fan-out as the text itself — the act read is
        // the only one that denormalises either — and the same reason it is not
        // `textAtom.create` / `textAtom.delete`: those invalidate every scene in
        // the project.
        addTextRow: [FOUNTAIN_ACTS],
        removeTextRow: [FOUNTAIN_ACTS],
    },
    fragment: {
        create: [FOUNTAIN_FRAGMENTS],
        update: [FOUNTAIN_FRAGMENTS],
        delete: [FOUNTAIN_FRAGMENTS, FOUNTAIN_SCENES],
        updateLogline: [FOUNTAIN_FRAGMENTS],
        updateSynopsis: [FOUNTAIN_FRAGMENTS],
        // The fragment's half of the act rows above, same reasoning.
        setDraft: [FOUNTAIN_FRAGMENTS],
        addTextRow: [FOUNTAIN_FRAGMENTS],
        removeTextRow: [FOUNTAIN_FRAGMENTS],
    },
    character: {
        create: [CHARACTERS, TAGS],
        // Renames propagate to the character's entity_tag, so tag caches refresh too.
        // RELATIONSHIPS: every relation embeds both characters' name/category,
        // and deleting a character deletes the relations it owns.
        update: [CHARACTERS, TAGS, RELATIONSHIPS],
        delete: [CHARACTERS, TAGS, SCENES, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, RELATIONSHIPS],
    },
    // Character relations (#1844). Store/update answer with the bare model,
    // not the index shape, so the response is never written into the cache —
    // the read refetches. No character read renders a relation (the detail
    // payload embeds `character_relations`, nothing reads it), so CHARACTERS
    // stays out.
    relationship: {
        create: [RELATIONSHIPS],
        update: [RELATIONSHIPS],
        delete: [RELATIONSHIPS],
    },
    // A project's own relationship types (#1847). Deleting one leaves every
    // relation of that type untyped on the server (nullOnDelete), so the
    // relations refetch too.
    relationType: {
        create: [RELATION_TYPES],
        delete: [RELATION_TYPES, RELATIONSHIPS],
    },
    episode: {
        create: [EPISODES, FOUNTAIN_FRAGMENTS, EPISODES_SEARCH, EPISODE_PRESENTATIONS, EPISODE_PRESENTATIONS_TITLE],
        update: [
            EPISODES,
            SCENES,
            FOUNTAIN_FRAGMENTS,
            EPISODES_SEARCH,
            EPISODE_PRESENTATIONS,
            EPISODE_PRESENTATIONS_TITLE,
            LINE_PRESENTATIONS,
        ],
        delete: [EPISODES, FOUNTAIN_FRAGMENTS, EPISODES_SEARCH, EPISODE_PRESENTATIONS, EPISODE_PRESENTATIONS_TITLE],
    },
    // Features carry an entity_tag (location chips), so tag caches refresh on
    // every action; filter lists surface feature names.
    feature: {
        create: [FEATURES, FILTERS, TAGS],
        update: [FEATURES, FILTERS, TAGS],
        delete: [FEATURES, FOUNTAIN_SCENES, FILTERS, TAGS],
    },
    tag: {
        create: [TAGS],
        update: [TAGS],
        // Colors surface wherever the tag is denormalised: scene cards, episode
        // and line presentations (union of the pre-ADR-0013 inline fan-outs).
        updateColor: [TAGS, SCENES, EPISODE_PRESENTATIONS, LINE_PRESENTATIONS],
        delete: [TAGS],
    },
    bit: {
        create: [BITS],
        update: [BITS],
        delete: [BITS],
    },
    // Text atoms live inside scenes; edits also reach the queries that embed
    // scene text (fountain scenes/fragments), comment anchors, and the episode
    // presentations that aggregate scene durations. This row replaced the
    // invalidateEditorCaches shotgun (8 roots + refetchType 'all') — see the
    // characterization pin in textAtomCacheManager.test.ts (ADR 0010 stage 3).
    textAtom: {
        create: [SCENES, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, COMMENTS, EPISODES],
        update: [SCENES, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, COMMENTS, EPISODES],
        delete: [SCENES, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, COMMENTS, EPISODES],
    },
    // «Заметки» are `scene_note` text atoms, but they invalidate nothing a
    // scene body query is keyed under — deliberately. A note's body is not part
    // of the screenplay: it never reaches export, pagination reads it from the
    // engine, and the marker that places it lives in scene text that a note
    // write never touches. Sharing the textAtom row would refetch every scene
    // in the project on each note keystroke, and re-sync the document mid-edit.
    sceneNote: {
        create: [SCENE_NOTES],
        // Purpose-named: the only thing an update ever changes is the note's
        // own content (body, quote, stamps). There is no note action that
        // reaches another noun, which is why this row has no second prefix.
        updateBody: [SCENE_NOTES],
        delete: [SCENE_NOTES],
    },
    // «Заметки» on an act / episode header row (#1840-18) — `act_note` /
    // `fragment_note` atoms. Unlike a scene note they have no cache root of
    // their own: the atoms ride the act / fragment payloads the header rows are
    // already rendered from (`data/hostedNoteAtoms.ts`), so the list that goes
    // stale IS the entity list. That is one small refetch per write, where
    // sharing `textAtom.update` would pull every scene in the project back.
    //
    // Two families rather than one, for the reason `setDraft` is two rows: an
    // act's notes and a fragment's notes are two different reads, and each
    // write should say only its own is stale.
    actNote: {
        create: [FOUNTAIN_ACTS],
        updateBody: [FOUNTAIN_ACTS],
        delete: [FOUNTAIN_ACTS],
    },
    fragmentNote: {
        create: [FOUNTAIN_FRAGMENTS],
        updateBody: [FOUNTAIN_FRAGMENTS],
        delete: [FOUNTAIN_FRAGMENTS],
    },
    project: {
        create: [PROJECTS],
        update: [PROJECTS, COMMENTS],
        // Each moves a project between the active, archived and deleted
        // lists — all under PROJECTS, so both ends of the move refetch.
        delete: [PROJECTS],
        archive: [PROJECTS],
        restore: [PROJECTS],
    },
    comment: {
        create: [COMMENTS],
        createChild: [COMMENTS],
        update: [COMMENTS],
        delete: [COMMENTS],
    },
    notification: {
        update: [NOTIFICATIONS],
        delete: [NOTIFICATIONS],
    },
    snapshot: {
        create: [SNAPSHOTS],
    },
    characterLine: {
        create: [LINE_PRESENTATIONS],
        update: [LINE_PRESENTATIONS],
        delete: [LINE_PRESENTATIONS],
    },
    // RELATIONSHIPS: relations embed both characters' avatars.
    asset: {
        create: [ASSETS, CHARACTERS, FEATURES, RELATIONSHIPS],
        update: [ASSETS, CHARACTERS, FEATURES, RELATIONSHIPS],
        delete: [ASSETS, CHARACTERS, FEATURES, RELATIONSHIPS],
    },
    // The global actors dictionary + project attachments (2026-09-03 backend).
    // The casting tab reads extras-enriched characters, which real actor
    // writes never change — ACTORS covers the future dictionary-search reads
    // (quick-fill). Attach/detach also mutate project->actors, which
    // ProjectResource now embeds.
    actor: {
        create: [ACTORS],
        update: [ACTORS],
        delete: [ACTORS],
        attach: [ACTORS, PROJECTS],
        detach: [ACTORS, PROJECTS],
    },
    line: {
        create: [LINES, LINE_PRESENTATIONS],
        update: [LINES, LINE_PRESENTATIONS, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, FOUNTAIN_ACTS],
        delete: [LINES, LINE_PRESENTATIONS, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, FOUNTAIN_ACTS],
    },
    // «Добавить в словарь»: the project's shared dictionary. A word is no
    // part of any other payload — the speller reads this list alone.
    spellingWord: {
        create: [SPELLING_WORDS],
    },
    // `user.tutorial` (#1854): read by nothing but its own fetcher, even once
    // it rides on `GET user` (see USER_TUTORIAL_ROOT).
    userTutorial: {
        update: [USER_TUTORIAL],
    },
};

/**
 * Cross-cutting actions that span multiple entities. Modeled separately
 * from the per-entity registry because they don't fit the entity.action shape.
 */
export const crossCutting = {
    // EPISODE_PRESENTATIONS: the character page's «Сцены с персонажем» tab
    // reads episodePresentations/character — attaching or detaching a
    // character tag on a scene must refresh it (QA, 2026-08-25).
    'entityTag.attach': [FOUNTAIN_ACTS, FOUNTAIN_FRAGMENTS, FOUNTAIN_SCENES, CHARACTERS, FEATURES, EPISODE_PRESENTATIONS] as PrefixList,
    // Script import creates characters that the union dialog reads immediately;
    // everything else the import touches refetches via the SSE completion flow.
    'project.import': [CHARACTERS] as PrefixList,
    // AI logline/synopsis generation (#1838) rewrites per-scene metadata that
    // every scene-bearing read denormalises — raw scenes, the three fountain
    // presentations, the episode/plan presentations. Fired once, when the
    // job's SSE stream ends (completed or not — a failed job may have written
    // part of the script before it stopped).
    'project.generateResume': [SCENES, FOUNTAIN_SCENES, FOUNTAIN_FRAGMENTS, FOUNTAIN_ACTS, EPISODES, EPISODE_PRESENTATIONS] as PrefixList,
} as const;

export type CrossCuttingAction = keyof typeof crossCutting;

/**
 * Resolve invalidation prefixes for an entity action. Used internally by the
 * generated hooks; exported for the inspect helper.
 */
export function getPrefixes<E extends EntityName>(entity: E, action: EntityActions[E]): PrefixList {
    return (registry[entity] as Record<string, PrefixList>)[action as string] ?? [];
}

export function getCrossCuttingPrefixes(action: CrossCuttingAction): PrefixList {
    return crossCutting[action];
}
